import React, { useEffect, useMemo, useState } from 'react';
import { connect } from 'react-redux';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  MenuItem,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from '@material-ui/core';
import EditIcon from '@material-ui/icons/Edit';
import DeleteIcon from '@material-ui/icons/Delete';
import HttpService from '../../../services/httpService';
import { setLoading, setSnackInfo } from '../../../redux/actions/appAction';

function parseCityList(response) {
  const raw = response?.data;
  if (Array.isArray(raw)) return raw;
  if (raw && Array.isArray(raw.data)) return raw.data;
  return [];
}

const defaultForm = {
  name: '',
  kekaCityId: '',
};

function KekaExperienceCenters(props) {
  const [rows, setRows] = useState([]);
  const [cities, setCities] = useState([]);
  const [openForm, setOpenForm] = useState(false);
  const [openDelete, setOpenDelete] = useState(false);
  const [editId, setEditId] = useState('');
  const [deleteId, setDeleteId] = useState('');
  const [form, setForm] = useState(defaultForm);

  const cityOptions = useMemo(
    () =>
      cities.map((c) => ({
        _id: c?._id,
        name: c?.name || '',
      })),
    [cities]
  );

  const loadPageData = async () => {
    props.setLoading(true);
    try {
      const [ecRes, citiesRes] = await Promise.all([
        HttpService.getKekaExperienceCenters(),
        HttpService.getKekaCities(),
      ]);
      const ecList = Array.isArray(ecRes?.data) ? ecRes.data : [];
      const cityList = parseCityList(citiesRes);
      setRows(ecList);
      setCities(cityList);
    } catch (error) {
      props.setSnackInfo(
        error?.response?.data?.message || 'Error loading keka experience centers',
        'error'
      );
      setRows([]);
      setCities([]);
    } finally {
      props.setLoading(false);
    }
  };

  useEffect(() => {
    void loadPageData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleOpenCreate = () => {
    setEditId('');
    setForm(defaultForm);
    setOpenForm(true);
  };

  const handleOpenEdit = (item) => {
    setEditId(item?._id || '');
    setForm({
      name: item?.name || '',
      kekaCityId: item?.kekaCityId?._id || item?.kekaCityId || '',
    });
    setOpenForm(true);
  };

  const validateForm = () => {
    if (!String(form.name || '').trim()) return 'Experience center name is required';
    if (!String(form.kekaCityId || '').trim()) return 'Keka city is required';
    return '';
  };

  const handleSave = async () => {
    const validationError = validateForm();
    if (validationError) {
      props.setSnackInfo(validationError, 'error');
      return;
    }

    props.setLoading(true);
    try {
      const payload = {
        name: String(form.name || '').trim(),
        kekaCityId: form.kekaCityId,
      };
      if (editId) {
        await HttpService.updateKekaExperienceCenter(editId, payload);
        props.setSnackInfo('Keka experience center updated successfully', 'success');
      } else {
        await HttpService.createKekaExperienceCenter(payload);
        props.setSnackInfo('Keka experience center created successfully', 'success');
      }
      setOpenForm(false);
      setEditId('');
      setForm(defaultForm);
      await loadPageData();
    } catch (error) {
      props.setSnackInfo(
        error?.response?.data?.message || 'Error saving keka experience center',
        'error'
      );
    } finally {
      props.setLoading(false);
    }
  };

  const handleDelete = async () => {
    if (!deleteId) return;
    props.setLoading(true);
    try {
      await HttpService.deleteKekaExperienceCenter(deleteId);
      props.setSnackInfo('Keka experience center deactivated', 'success');
      setOpenDelete(false);
      setDeleteId('');
      await loadPageData();
    } catch (error) {
      props.setSnackInfo(
        error?.response?.data?.message || 'Error deleting keka experience center',
        'error'
      );
    } finally {
      props.setLoading(false);
    }
  };

  return (
    <Box p={2}>
      <Box
        mb={2}
        display="flex"
        alignItems="center"
        justifyContent="space-between"
      >
        <Typography variant="h6" style={{ fontWeight: 700 }}>
          Keka Experience Centers
        </Typography>
        <Button
          variant="contained"
          style={{ backgroundColor: '#F47E46', color: '#fff' }}
          onClick={handleOpenCreate}
        >
          Add Keka Experience Center
        </Button>
      </Box>

      <TableContainer component={Paper}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Sr. No.</TableCell>
              <TableCell>Name</TableCell>
              <TableCell>City</TableCell>
              <TableCell>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.length === 0 ? (
              <TableRow>
                <TableCell colSpan={4} align="center">
                  No keka experience centers found
                </TableCell>
              </TableRow>
            ) : (
              rows.map((row, index) => (
                <TableRow key={row?._id || index}>
                  <TableCell>{index + 1}</TableCell>
                  <TableCell>{row?.name || '-'}</TableCell>
                  <TableCell>{row?.kekaCityId?.name || '-'}</TableCell>
                  <TableCell>
                    <Tooltip title="Edit">
                      <IconButton
                        size="small"
                        onClick={() => handleOpenEdit(row)}
                      >
                        <EditIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Delete">
                      <IconButton
                        size="small"
                        style={{ color: '#d32f2f' }}
                        onClick={() => {
                          setDeleteId(row?._id || '');
                          setOpenDelete(true);
                        }}
                      >
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      <Dialog
        open={openForm}
        onClose={() => setOpenForm(false)}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>
          {editId ? 'Edit Keka Experience Center' : 'Add Keka Experience Center'}
        </DialogTitle>
        <DialogContent>
          <Box mt={1} display="grid" gridGap={16}>
            <TextField
              label="Experience Center Name"
              variant="outlined"
              size="small"
              fullWidth
              value={form.name}
              onChange={(e) => setForm((prev) => ({ ...prev, name: e.target.value }))}
            />
            <TextField
              label="Keka City"
              variant="outlined"
              size="small"
              fullWidth
              select
              value={form.kekaCityId}
              onChange={(e) =>
                setForm((prev) => ({ ...prev, kekaCityId: e.target.value }))
              }
            >
              <MenuItem value="">
                <em>Select City</em>
              </MenuItem>
              {cityOptions.map((c) => (
                <MenuItem key={c._id} value={c._id}>
                  {c.name}
                </MenuItem>
              ))}
            </TextField>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenForm(false)} color="default">
            Cancel
          </Button>
          <Button onClick={handleSave} color="primary" variant="contained">
            {editId ? 'Update' : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={openDelete}
        onClose={() => setOpenDelete(false)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>Delete Keka Experience Center</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to deactivate this experience center?
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenDelete(false)} color="default">
            Cancel
          </Button>
          <Button onClick={handleDelete} color="secondary" variant="contained">
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

export default connect(null, { setLoading, setSnackInfo })(KekaExperienceCenters);
